import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { action, observable } from "mobx";
import { observer } from "mobx-react";
import { MXP_EVENT } from "Static/MixPanelEvents";
import makeApi, { URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import { ReactComponent as ForwardArrowSvg } from "Assets/svg/arrow-forward.svg";
import { ReactComponent as ResetCreditsSvg } from "Assets/svg/creditsOverview/resetcredits.svg";
import { ReactComponent as RemoveDailyLimitSvg } from "Assets/svg/creditsOverview/removeDailyLimit.svg";
import { ReactComponent as Check } from "Assets/Pricing/pricingCheck.svg";
import userDetail from "Stores/userDetail";
import PricingStore from "Stores/Pricing";

class CreditUsagePopupState {
  @observable creditsData = { dailyLimit: true };
  @observable showPopup = false;
  @observable creditsDataLoading = true;
  @observable upgradeToRemoveDailyLimit = false;
  @observable isCreditsLoading = false;

  @action
  setCreditsLoading = (value = {}) => {
    this.isCreditsLoading = value;
  };

  @action
  setCreditsData = (value = {}) => {
    this.creditsData = value;
  };

  @action
  setCreditsDataLoading = (value = false) => {
    this.creditsDataLoading = value;
  };

  @action
  setShowPopup = (value = false) => {
    this.showPopup = value;
  };

  @action
  setUpgradeToRemoveDailyLimit = (value = false) => {
    this.upgradeToRemoveDailyLimit = value;
  };

  @action
  getCreditsUsage = async () => {
    try {
      const config = {
        url: URLS.getCreditsUsage
      };
      const res = await makeApi(config);
      setTimeout(() => {
        this.setCreditsDataLoading(false);
      }, 500);
      if (res?.data && Object.keys(res?.data)?.length > 0) {
        this.setCreditsData(res?.data);
      }
    } catch (err) {
      console.error(err, "Getting Credit Usage ERROR!");
    }
    return;
  };
}

const creditUsagePopupState = new CreditUsagePopupState();

const LinearProgress = ({ width = 0, bgColor = "#38B892" }) => {
  return (
    <div className="linearProgressBar">
      <div className="backgroundProgress"></div>
      <div
        className={`linearProgress`}
        style={{
          width: `${width}%`,
          background: `${bgColor}`
        }}
      ></div>
    </div>
  );
};

const CreditsGrid = observer((props = {}) => {
  const {
    limitTitle = "Daily",
    total = 0,
    used = 0,
    remaining = 0,
    bgColor = "",
    isEmailCredits = false,
    inFreePlan = false
  } = props || {};

  return (
    <div className="creditsGridWrapper">
      <div className="flexWrapper">
        <p className="creditsLimitTitle">{limitTitle}</p>
      </div>
      <div className="linearProgressWrapper">
        {creditUsagePopupState.creditsDataLoading ? (
          <div className="linear-background"></div>
        ) : (
          <LinearProgress
            width={`${(remaining / total) * 100}`}
            bgColor={bgColor}
          />
        )}
      </div>
      {(remaining > 0 || creditUsagePopupState.creditsDataLoading) && (
        <>
          {inFreePlan &&
            limitTitle.toLowerCase() === "monthly" &&
            isEmailCredits && (
              <div className="freeUserCredits">
                <div className="freeUserCreditsRenew">
                  {`(100 free credits renew on the 1st of every month)`}
                </div>
              </div>
            )}
          <div className="remainingCountWrapper">
            <div className={"redeemCount"}>
              <div
                className={`${
                  creditUsagePopupState.creditsDataLoading
                    ? "linear-background"
                    : ""
                }`}
              >
                {!creditUsagePopupState.creditsDataLoading &&
                  `(${Utils.formatNumberToString(
                    total
                  )} credits renew on the 1st of every month)`}
              </div>
            </div>
            <div className={"remainingCount"}>
              <div
                className={`${
                  creditUsagePopupState.creditsDataLoading
                    ? "linear-background"
                    : ""
                }`}
              >
                {!creditUsagePopupState.creditsDataLoading &&
                  `${Utils.formatNumberToString(remaining)} remaining`}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const CreditsConsumption = () => {
  const creditsConsumption = [
    { count: 1, type: "Email Address", credits: 1 },
    { count: 1, type: "Phone number", credits: 10 },
    { count: 1, type: "Deep Research", credits: 20 }
  ];

  return (
    <div className="phoneCreditsWrapper">
      <div className="creditsConsumptionGridWrapper">
        <div className="creditsSectionHeader">
          <span>How credits are used</span>
        </div>
        <div className="creditsUsageRowWrapper">
          {creditsConsumption?.map((creditsItem, idx) => {
            const { count = 0, type = "", credits = "" } = creditsItem || {};
            return (
              <div className="creditsUsageRow" key={idx}>
                <span>
                  <Check /> {`${count} ${type}`}
                </span>
                <span>
                  {`${credits} ${credits > 1 ? "credits" : "credit"}`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MonthlyCredits = observer(({}) => {
  const creditsData = creditUsagePopupState.creditsData || {};

  const {
    creditsEligible = 0,
    creditsRemaining = 0,
    creditsUsed = 0
  } = creditsData || {};

  return (
    <div className="phoneCreditsWrapper">
      <CreditsGrid
        limitTitle={"Monthly"}
        total={creditsEligible}
        used={creditsUsed}
        remaining={creditsRemaining}
        bgColor="#1EB9D5"
      />
    </div>
  );
});

const CreditUsagePopup = observer(() => {
  const history = useHistory();
  const { inFreePlan = false } = userDetail?.userFeatureFlag || {};
  const creditsData = creditUsagePopupState.creditsData || {};

  const handlePricingOrPayment = () => {
    /* Commented condition is free plan pushed to /pricing/payment but that path available only for paid users, and daily limit condition? */
    // if (inFreePlan) {
    //   let unlimitedPlan = PricingStore?.activePlanData.filter(
    //     item => item.displayName === "Unlimited"
    //   )[0];

    //   PricingStore.setPaymentFormInfo(unlimitedPlan);
    //   localStorage.setItem("proceedToPay", true);
    //   history.push("/pricing/payment");
    // } else {
    // if (creditsData?.dailyLimit) {
    //   history.push("/pricing");
    // }
    // }
    history.push("/pricing");
    creditUsagePopupState.setShowPopup(false);
  };

  useEffect(() => {
    if (creditUsagePopupState.showPopup) {
      creditUsagePopupState.setCreditsDataLoading(true);
      creditUsagePopupState.getCreditsUsage();
      Utils.mixpanelTrack(MXP_EVENT.CREDITS_HOVER_POPUP);
    }
  }, [creditUsagePopupState.showPopup]);

  useEffect(() => {
    PricingStore.getActivePlanDetails();
    PricingStore.getDataOutreachPlans();
  }, []);

  return (
    creditUsagePopupState.showPopup && (
      <div>
        <div
          className="creditUsageMask"
          onMouseEnter={() => creditUsagePopupState.setShowPopup(true)}
          onMouseLeave={() => creditUsagePopupState.setShowPopup(false)}
        />
        <div
          className={`creditUsagePopupWrapper ${
            creditUsagePopupState.upgradeToRemoveDailyLimit
              ? "removeDailyLimitWrapper"
              : ""
          } `}
          onMouseEnter={() => creditUsagePopupState.setShowPopup(true)}
          onMouseLeave={() => creditUsagePopupState.setShowPopup(false)}
        >
          <h4>Credits overview</h4>
          {creditUsagePopupState.creditsDataLoading ? (
            <div className="creditsUsageHead linear-background"></div>
          ) : (
            creditsData?.creditsRemaining > 0 && (
              <div
                className={`creditsUsageHead ${
                  creditUsagePopupState.upgradeToRemoveDailyLimit
                    ? "borderHeader"
                    : ""
                }`}
                onClick={() => {
                  handlePricingOrPayment();
                }}
                onMouseEnter={() =>
                  creditUsagePopupState.setUpgradeToRemoveDailyLimit(true)
                }
                onMouseLeave={() =>
                  creditUsagePopupState.setUpgradeToRemoveDailyLimit(false)
                }
              >
                {creditUsagePopupState.upgradeToRemoveDailyLimit ? (
                  <div
                    className="removeDailyLimit"
                    onClick={() => {
                      Utils.mixpanelTrack(MXP_EVENT.REMOVE_DAILY_LIMIT);
                      handlePricingOrPayment();
                    }}
                  >
                    <span className="resetCreditsIcon">
                      <RemoveDailyLimitSvg />
                    </span>
                    <p className="limitText">{"Get More Credits"}</p>
                    <span className="arrowIcon">
                      <ForwardArrowSvg />
                    </span>
                  </div>
                ) : (
                  <>
                    <span className="resetCreditsIcon">
                      <ResetCreditsSvg />
                    </span>
                    <p className="limitText">
                      {`${Utils.formatNumberToString(
                        creditsData?.creditsRemaining
                      )} credits left. Buy more credits.`}
                    </p>
                  </>
                )}
              </div>
            )
          )}
          <div className="creditUsagePopupContent">
            <MonthlyCredits handlePricingOrPayment={handlePricingOrPayment} />
            <CreditsConsumption
              handlePricingOrPayment={handlePricingOrPayment}
            />
          </div>
          <div
            className="creditFooterText"
            onClick={() => {
              creditUsagePopupState.setShowPopup(false);
              history.push("/settings/creditUsage");
            }}
          >
            <span className="">View details in credit history</span>
            <ForwardArrowSvg />
          </div>
        </div>
      </div>
    )
  );
});

export { CreditUsagePopup, creditUsagePopupState };
export default CreditUsagePopup;
